import { getAnalyticsQuestionName } from '../../survey/analyticsNames';
import { stepsDetails } from '../../survey/configs/stepsDetails';
import { useQuestionsStore } from '../../store';

export type UserData = Record<string, string | string[]>;

type StoredAnswer = {
  value?: unknown;
  a_value?: unknown;
};

// customV2_email — единственный шаг, который пишет ответ в вопрос с другим именем
// ('mail'), см. navigateForward. Ключ и признак пройденности берём по шагу, иначе
// вопрос 'mail' получил бы легаси-имя аналитики 'Mail' вместо 'mail'.
const STEP_BY_QUESTION: Record<string, string> = {
  mail: 'customV2_email',
};

const NAME_KEY = 'name';
const BREED_KEY = 'breed';
const CHOICE_BREED_KEY = 'choice_breed';
const FALLBACK_BREED = 'mixed_breed';

// Пропущенные name/breed уходят пустой строкой, а не 'skip'.
const EMPTY_ON_SKIP_KEYS = new Set([NAME_KEY, BREED_KEY]);

const BREED_WHITELIST = new Set([
  'chihuahua',
  'dachshund',
  'french_bulldog',
  'german_shepherd',
  'golden_retriever',
  'labrador_retriever',
  'maltipoo',
  'mixed_breed',
  'poodle',
  'shih_tzu',
  'yorkshire_terrier',
]);

// Шаги, с которых пользователь ушёл вперёд. Нужны, чтобы отличить пропущенный
// вопрос (отдаём 'skip') от шага, до которого пользователь просто не дошёл.
const passedSteps = new Set<string>();

export function markStepPassed(step: string): void {
  if (step) passedSteps.add(step);
}

function isInfoStep(step: string): boolean {
  const details = stepsDetails[step];

  return !!(details?.continue || details?.continueAuto || details?.welcome_loader);
}

function isAnswerObject(answer: unknown): answer is StoredAnswer {
  return typeof answer === 'object' && answer !== null;
}

function toAnswerValue(value: unknown): string | null {
  if (typeof value === 'string' && value !== '') return value;
  if (typeof value === 'number') return String(value);
  return null;
}

// Аналитика отправляет ответ на customV2_breed в snake_case, см. stepsUpdated.
function normalizeValue(questionName: string, value: string): string {
  if (questionName === 'customV2_breed') return value.toLowerCase().replace(/ /g, '_');

  return value;
}

function getQuestionValues(questionName: string, answers: Record<string, unknown>): string[] {
  const values: string[] = [];

  Object.entries(answers).forEach(([answerName, answer]) => {
    if (isAnswerObject(answer)) {
      const value = toAnswerValue(answer.a_value) || toAnswerValue(answer.value);

      if (value) values.push(normalizeValue(questionName, value));
      return;
    }

    if (answerName === 'value') {
      const value = toAnswerValue(answer);

      if (value) values.push(normalizeValue(questionName, value));
    }
  });

  return values;
}

export default function getUserData(): UserData | null {
  const { list } = useQuestionsStore.getState();

  if (!list) return null;

  const userData: UserData = {};

  Object.entries(list).forEach(([questionName, question]) => {
    const stepName = STEP_BY_QUESTION[questionName] || questionName;
    const key = getAnalyticsQuestionName(stepName) || stepName;
    const values = getQuestionValues(questionName, (question.answers || {}) as Record<string, unknown>);

    if (values.length) {
      userData[key] = values.length === 1 ? values[0] : values;
      return;
    }

    if (!passedSteps.has(stepName)) return;
    if (isInfoStep(stepName)) return;
    if (userData[key] !== undefined) return;

    userData[key] = EMPTY_ON_SKIP_KEYS.has(key) ? '' : 'skip';
  });

  if (!Object.keys(userData).length) return null;

  const breed = userData[BREED_KEY];

  userData[CHOICE_BREED_KEY] = typeof breed === 'string' && BREED_WHITELIST.has(breed) ? breed : FALLBACK_BREED;

  return userData;
}
